﻿import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, Save, X, CheckCircle, Edit2, Video,
  Wifi, WifiOff, User, Download, FileText, Car,
  UploadCloud, AlertCircle, Upload, Trash2, Link as LinkIcon, Plus,
  Image as ImageIcon, MapPin, Eye, ChevronRight,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { VIDEO_EXT_WHITELIST } from '../lib/storage';
import PhotoGallery from '../components/PhotoGallery';
import CctvWall from '../components/CctvWall';
import { useApp } from '../contexts/AppContext';
import { getIntelLabel } from '../lib/statusLabel';
import { syncMissingEmbeddings } from '../lib/aiScanner';

const MAX_VIDEO_SIZE = 500 * 1024 * 1024;
const MAX_IMAGE_SIZE = 20 * 1024 * 1024;
const MAX_CSV_SIZE   = 10 * 1024 * 1024;
const IMAGE_EXT_WHITELIST = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'];

// ── Least-loaded camera picker ──────────────────────────────────────────────
// Untuk distribusi video upload ke kamera yang masih kosong / paling sedikit
// playlistnya. Skip kamera live stream (stream_url) karena tidak punya playlist.
// Sort ascending by playlist.length (count) → ambil kamera dengan jumlah video
// paling rendah; bila ada beberapa, pakai urutan abjad nama.
export function pickLeastLoadedCamera(kameras) {
  const candidates = (kameras || [])
    .filter(k => !k.stream_url)
    .map(k => ({ k, count: (k.playlist?.length ?? 0) }))
    .sort((a, b) => a.count - b.count || (a.k.nama || '').localeCompare(b.k.nama || ''));
  return candidates[0]?.k ?? null;
}

function safeStoragePath(url, bucket) {
  if (typeof url !== 'string') return null;
  const marker = `/${bucket}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  const raw = url.slice(idx + marker.length).split('?')[0];
  let decoded;
  try { decoded = decodeURIComponent(raw); } catch { decoded = raw; }
  if (!decoded || decoded.includes('..') || decoded.startsWith('/')) return null;
  return decoded;
}

// ── CSV Templates ─────────────────────────────────────────────────────────────

const PENDUDUK_HEADERS = [
  'nik','nama_lengkap','tempat_lahir','tanggal_lahir','jenis_kelamin',
  'gol_darah','agama','status_perkawinan','pekerjaan','kewarganegaraan',
  'alamat_lengkap','rt_rw','kel_desa','kecamatan','kota_kabupaten',
  'status_intelijen','catatan_fisik',
];

const PENDUDUK_EXAMPLE = [
  '6402060311960001','BUDI SANTOSO','JAKARTA','1990-01-15','LAKI-LAKI',
  'A','Islam','KAWIN','KARYAWAN SWASTA','WNI',
  'JL. MERDEKA NO. 1','001/002','GAMBIR','GAMBIR','JAKARTA PUSAT',
  'CLEAR','',
];

const KENDARAAN_HEADERS = [
  'no_plat','merk_model','warna','tipe_kendaraan','tahun',
  'no_mesin','no_rangka',
  'masa_berlaku_stnk','catatan_visual','nik_pemilik',
];

const KENDARAAN_EXAMPLE = [
  'B 1234 ABC','Toyota Avanza','Putih','Mobil',
  'MX1234567','RX7654321',
  '2026-12-31','Kaca gelap, ban racing','6402060311960001',
];

function makeCSV(headers, example, extraRows = []) {
  const lines = [headers.join(','), example.join(','), ...extraRows.map(r => r.join(','))];
  return lines.join('\n');
}

function triggerDownload(filename, content) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadPendudukTemplate() {
  triggerDownload('template_penduduk.csv', makeCSV(PENDUDUK_HEADERS, PENDUDUK_EXAMPLE));
}

function downloadKendaraanTemplate() {
  triggerDownload('template_kendaraan.csv', makeCSV(KENDARAAN_HEADERS, KENDARAAN_EXAMPLE));
}

// ── Batch CSV Import ──────────────────────────────────────────────────────────

function parseCSVLine(line) {
  const result = []; let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      // Handle "" as escaped quote inside quoted field
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; continue; }
      inQ = !inQ; continue;
    }
    if (c === ',' && !inQ) { result.push(cur); cur = ''; continue; }
    cur += c;
  }
  result.push(cur);
  return result;
}

function parseCSV(text, defaultStatus, allow) {
  // Strip BOM (Excel sering menambah ﻿ di header pertama → 'nik' jadi tak
  // dikenal → seluruh upsert data:null senyap).
  const lines = text.replace(/^﻿/, '').replace(/\r/g, '').split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];
  const allowSet = allow ? new Set(allow) : null;
  const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const vals = parseCSVLine(line);
    const obj = defaultStatus ? { status_intelijen: defaultStatus } : {};
    headers.forEach((h, i) => {
      // Abaikan kolom di luar allowlist → cegah kolom tak dikenal membuat
      // seluruh chunk gagal ditulis tanpa error (silent data-loss).
      if (allowSet && !allowSet.has(h)) return;
      obj[h] = vals[i]?.trim() || null;
    });
    return obj;
  }).filter(r => r.nik || r.no_plat);
}

const STATUS_CFG_BATCH = {
  WANTED: '#ff2d55', SUSPECT: '#ff6b2b', WATCHLIST: '#ffd60a', CLEAR: 'var(--color-green)',
};

const BatchImportModal = ({ type, onClose, onImported }) => {
  const isPenduduk = type === 'penduduk';
  const [step, setStep]         = useState('upload');
  const [rows, setRows]         = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults]   = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > MAX_CSV_SIZE) {
      alert(`File terlalu besar (${(file.size / 1024 / 1024).toFixed(1)} MB). Maksimum 10 MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      const parsed = parseCSV(e.target.result, isPenduduk ? 'CLEAR' : null, isPenduduk ? PENDUDUK_HEADERS : KENDARAAN_HEADERS);
      if (!parsed.length) return alert('File kosong atau format tidak sesuai. Download template terlebih dahulu.');
      setRows(parsed);
      setStep('preview');
    };
    reader.onerror = () => alert('Gagal membaca file CSV.');
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    const table = isPenduduk ? 'penduduk' : 'kendaraan';
    const conflict = isPenduduk ? 'nik' : 'no_plat';
    const CHUNK = 50;
    let success = 0, failed = 0;
    for (let i = 0; i < rows.length; i += CHUNK) {
      const chunk = rows.slice(i, i + CHUNK);
      // .select() → hitung baris yang BENAR-BENAR ditulis, bukan asumsi
      // chunk.length (data:null saat kolom tak dikenal = 0 baris, bukan sukses).
      const { data, error } = await supabase.from(table).upsert(chunk, { onConflict: conflict }).select(conflict);
      const wrote = data?.length || 0;
      if (error || wrote === 0) { failed += chunk.length; }
      else { success += wrote; failed += (chunk.length - wrote); }
    }
    setImporting(false);
    setResults({ success, failed });
    setStep('done');
    if (success > 0) onImported(rows);
  };

  const headers = isPenduduk ? PENDUDUK_HEADERS : KENDARAAN_HEADERS;
  const previewCols = isPenduduk
    ? ['nik','nama_lengkap','tanggal_lahir','kota_kabupaten','status_intelijen']
    : ['no_plat','merk_model','warna','masa_berlaku_stnk','nik_pemilik'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(2,6,14,0.85)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-2xl rounded-3xl overflow-hidden flex flex-col"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', maxHeight: '88vh' }}>

        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border-dim)' }}>
          <div className="flex items-center gap-3">
            <Download size={15} color="var(--color-cyan)" />
            <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
              Import {isPenduduk ? 'Data Penduduk' : 'Data Kendaraan'}
              {step === 'preview' && <span style={{ color: 'var(--color-cyan)' }}> — {rows.length} baris</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => isPenduduk ? downloadPendudukTemplate() : downloadKendaraanTemplate()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black"
              style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', color: 'var(--color-green)' }}>
              <Download size={9} /> Template CSV
            </button>
            <button onClick={onClose}><X size={13} color="var(--text-dim)" /></button>
          </div>
        </div>

        {step === 'upload' && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 gap-5">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              className="w-full flex flex-col items-center justify-center gap-4 rounded-2xl cursor-pointer transition-all"
              style={{ height: 200, border: `2px dashed ${dragging ? 'var(--color-cyan)' : 'var(--border-mid)'}`, background: dragging ? 'rgba(0,212,255,0.04)' : 'var(--color-surface)' }}>
              <FileText size={44} color="rgba(0,212,255,0.25)" strokeWidth={1} />
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: 'var(--text-dim)' }}>Seret file CSV atau klik untuk pilih</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-mute)' }}>Gunakan format sesuai template · Maks 10.000 baris</p>
              </div>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => handleFile(e.target.files[0])} />
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl w-full"
              style={{ background: 'rgba(255,214,10,0.06)', border: '1px solid rgba(255,214,10,0.15)' }}>
              <AlertCircle size={12} color="#ffd60a" className="shrink-0 mt-0.5" />
              <p className="text-[9px]" style={{ color: 'var(--text-dim)' }}>
                Kolom <span style={{ color: '#ffd60a' }}>{isPenduduk ? 'nik' : 'no_plat'}</span> wajib diisi.
                Data dengan {isPenduduk ? 'NIK' : 'plat'} yang sudah ada akan diperbarui otomatis (upsert).
              </p>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="flex-1 overflow-auto c-scroll">
            <table className="w-full text-[9px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--color-surface)', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th className="px-3 py-2 text-left font-black" style={{ color: 'var(--text-mute)', borderBottom: '1px solid var(--border-dim)', width: 32 }}>#</th>
                  {previewCols.map(h => (
                    <th key={h} className="px-3 py-2 text-left font-black uppercase tracking-wider"
                      style={{ color: 'var(--text-mute)', borderBottom: '1px solid var(--border-dim)' }}>
                      {h.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-lo)' }}>
                    <td className="px-3 py-2 font-mono" style={{ color: 'var(--text-mute)' }}>{i + 1}</td>
                    {previewCols.map(col => (
                      <td key={col} className="px-3 py-2 max-w-40 truncate"
                        style={{ color: col === 'status_intelijen' ? (STATUS_CFG_BATCH[r[col]] || 'var(--color-green)') : col === 'nik' || col === 'no_plat' ? 'var(--text-dim)' : 'var(--text-mid)' }}>
                        {r[col] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {step === 'done' && results && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-center gap-2 p-5 rounded-2xl" style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)' }}>
                <p className="text-4xl font-black font-mono" style={{ color: 'var(--color-green)' }}>{results.success}</p>
                <p className="text-[9px] font-bold uppercase" style={{ color: 'var(--color-green)' }}>Berhasil Diimport</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-5 rounded-2xl" style={{ background: 'rgba(255,45,85,0.08)', border: '1px solid rgba(255,45,85,0.2)' }}>
                <p className="text-4xl font-black font-mono" style={{ color: '#ff2d55' }}>{results.failed}</p>
                <p className="text-[9px] font-bold uppercase" style={{ color: '#ff2d55' }}>Gagal</p>
              </div>
            </div>
            <button onClick={onClose}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black"
              style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
              <CheckCircle size={13} /> Selesai
            </button>
          </div>
        )}

        {step === 'preview' && (
          <div className="px-6 py-4 flex items-center justify-between shrink-0" style={{ borderTop: '1px solid var(--border-dim)' }}>
            <button onClick={() => setStep('upload')}
              className="px-4 py-2 rounded-xl text-xs font-black hover:opacity-70"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
              Ganti File
            </button>
            <button onClick={handleImport} disabled={importing}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black"
              style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)', opacity: importing ? 0.7 : 1 }}>
              {importing
                ? <><div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} /> Mengimport...</>
                : <><Download size={13} /> Import {rows.length} Data</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const STATUS_COLORS = {
  WANTED:    { color: '#ff2d55', bg: 'rgba(255,45,85,0.12)' },
  SUSPECT:   { color: '#ff6b2b', bg: 'rgba(255,107,43,0.10)' },
  WATCHLIST: { color: '#ffd60a', bg: 'rgba(255,214,10,0.10)' },
  CLEAR:     { color: 'var(--color-green)', bg: 'rgba(0,255,136,0.10)' },
};

// ─── Penduduk Tab ─────────────────────────────────────────────────────────────

const PendudukRow = ({ p, selected, onSelect }) => {
  const { lang } = useApp();
  const active = selected?.id === p.id;
  const sc = STATUS_COLORS[p.status_intelijen] || STATUS_COLORS.CLEAR;
  // Badge status embedding: DF (hijau) = siap deteksi, PROSES (amber) = sedang disiapkan, kosong = belum ada foto
  const hasEmb  = Array.isArray(p.face_embedding) && p.face_embedding.length > 0;
  const hasFoto = !!p.foto_url;
  return (
    <button onClick={() => onSelect(p)}
      className="w-full flex items-center gap-3 px-4 py-3 transition-all text-left"
      style={{ background: active ? 'rgba(0,212,255,0.07)' : 'transparent', borderBottom: '1px solid var(--border-lo)', borderLeft: `3px solid ${active ? 'var(--color-cyan)' : 'transparent'}` }}>
      <div className="w-9 h-9 rounded-xl shrink-0 overflow-hidden flex items-center justify-center"
        style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
        {p.foto_url
          ? <img src={p.foto_url} alt={p.nama_lengkap} className="w-full h-full object-cover" />
          : <User size={16} color="var(--text-mute)" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold truncate" style={{ color: 'var(--text-hi)' }}>{p.nama_lengkap}</p>
        <p className="text-[9px] font-mono truncate" style={{ color: 'var(--text-mute)' }}>{p.nik}</p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-[8px] font-black px-2 py-0.5 rounded-full"
          style={{ background: sc.bg, color: sc.color }}>{getIntelLabel(p.status_intelijen, lang)}</span>
        {hasEmb ? (
          <span className="text-[7px] font-black px-1.5 py-0.5 rounded-full"
            style={{ background: 'rgba(0,255,136,0.12)', color: 'var(--color-green)' }}>DF</span>
        ) : hasFoto ? (
          <span className="text-[7px] font-black px-1.5 py-0.5 rounded-full"
            style={{ background: 'rgba(255,214,10,0.12)', color: '#ffd60a' }}>PROSES</span>
        ) : null}
      </div>
    </button>
  );
};

const Field = ({ label, value, onChange, type = 'text', options, optionLabel }) => (
  <div>
    <p className="text-[8px] uppercase tracking-widest mb-1" style={{ color: 'var(--text-mute)' }}>{label}</p>
    {options
      ? <select value={value ?? ''} onChange={e => onChange(e.target.value)}
          className="w-full rounded-lg px-3 py-2 text-xs outline-none transition-all"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }}>
          {options.map(o => <option key={o} value={o}>{optionLabel ? optionLabel(o) : o}</option>)}
        </select>
      : <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
          className="w-full rounded-lg px-3 py-2 text-xs outline-none transition-all"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }} />
    }
  </div>
);

const EMPTY_VEHICLE_FORM = {
  no_plat: '',
  merk_model: '',
  tipe_kendaraan: '',
  warna: '',
  tahun: '',
  no_rangka: '',
  no_mesin: '',
  masa_berlaku_stnk: '',
  catatan_visual: '',
};

// ─── Dokumen Slot ────────────────────────────────────────────────────────────
// Upload dokumen identitas (KTP/Paspor/Akta/KK) ke bucket dokumen-penduduk.
// Path: {nik}/{type}.{ext} — ext otomatis dari MIME type.
const DOKUMEN_TYPES = [
  { key: 'dokumen_ktp_url',      type: 'ktp',      label: 'KTP'             },
  { key: 'dokumen_passport_url', type: 'passport', label: 'Paspor'          },
  { key: 'dokumen_akta_url',     type: 'akta',     label: 'Akta Lahir'      },
  { key: 'dokumen_kk_url',       type: 'kk',       label: 'Kartu Keluarga' },
];

const DokumenSlot = ({ nik, type, label, currentUrl, onChange }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const handlePick = async (file) => {
    if (!file || !nik) return;
    if (file.size > MAX_IMAGE_SIZE) { alert(`File terlalu besar (max ${MAX_IMAGE_SIZE/1024/1024}MB)`); return; }
    setBusy(true);
    try {
      const ext  = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `${nik}/${type}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('dokumen-penduduk').upload(path, file, { upsert: true, cacheControl: '3600' });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('dokumen-penduduk').getPublicUrl(path);
      const url = `${data.publicUrl}?v=${Date.now()}`; // cache-bust setelah replace
      onChange(url);
    } catch (e) {
      alert(`Upload gagal: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!currentUrl || !nik) return;
    if (!confirm(`Hapus dokumen ${label}?`)) return;
    try {
      // Cari semua ekstensi yang mungkin — hapus all
      for (const ext of ['jpg', 'jpeg', 'png', 'webp', 'pdf']) {
        await supabase.storage.from('dokumen-penduduk').remove([`${nik}/${type}.${ext}`]).catch(() => {});
      }
      onChange(null);
    } catch (e) {
      console.warn('[DokumenSlot] delete:', e?.message);
    }
  };

  return (
    <div className="rounded-lg p-2.5" style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <FileText size={11} color={currentUrl ? 'var(--color-cyan)' : 'var(--text-mute)'} />
          <p className="text-[9px] font-black font-mono" style={{ color: currentUrl ? 'var(--text-hi)' : 'var(--text-mute)' }}>
            {label}
          </p>
        </div>
        {currentUrl && (
          <button onClick={handleDelete} className="rounded p-0.5 hover:opacity-70" title="Hapus">
            <Trash2 size={10} color="#ff2d55" />
          </button>
        )}
      </div>
      {currentUrl ? (
        <div className="flex items-center gap-2">
          <a href={currentUrl} target="_blank" rel="noopener noreferrer"
             className="flex-1 text-[8px] font-mono truncate underline"
             style={{ color: 'var(--color-cyan)' }}>
            Buka file
          </a>
          <button onClick={() => inputRef.current?.click()} disabled={busy}
            className="text-[8px] font-mono px-1.5 py-0.5 rounded hover:opacity-80"
            style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)' }}>
            {busy ? '...' : 'Ganti'}
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} disabled={busy || !nik}
          className="w-full text-[8px] font-mono py-1.5 rounded hover:opacity-80 transition-opacity"
          style={{
            background: nik ? 'rgba(0,212,255,0.06)' : 'transparent',
            border: '1px dashed ' + (nik ? 'rgba(0,212,255,0.3)' : 'var(--border-dim)'),
            color: nik ? 'var(--color-cyan)' : 'var(--text-mute)',
          }}>
          {busy ? 'Mengupload...' : (nik ? '+ Upload ' + label : 'Simpan penduduk dulu')}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden"
             onChange={e => handlePick(e.target.files?.[0])} />
    </div>
  );
};

const EditPanel = ({ penduduk, onSaved, onClose, isNew = false }) => {
  const { t, lang } = useApp();
  const [form, setForm] = useState({ ...penduduk });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState(EMPTY_VEHICLE_FORM);
  const [vehicleSaving, setVehicleSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    if (penduduk.nik && !isNew) {
      setVehicleLoading(true);
      supabase.from('kendaraan')
        .select('no_plat,merk_model,tipe_kendaraan,warna,tahun,no_rangka,no_mesin,masa_berlaku_stnk,catatan_visual')
        .eq('nik_pemilik', penduduk.nik)
        .then(({ data, error }) => {
          if (!alive) return;
          if (error) console.warn('[EditPanel] kendaraan:', error.message);
          setVehicles(data || []);
          setVehicleLoading(false);
        });
    }
    return () => { alive = false; };
  }, [penduduk.nik]);

  const stnkOk = (masa) => masa ? new Date(masa) >= new Date() : null;
  const fmtStnk = (masa) => masa
    ? new Date(masa).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
    : '-';

  const openAddVehicle = () => {
    setEditingVehicle(null);
    setVehicleForm(EMPTY_VEHICLE_FORM);
    setShowVehicleForm(true);
  };
  const openEditVehicle = (v) => {
    setEditingVehicle(v);
    setVehicleForm({
      no_plat: v.no_plat || '',
      merk_model: v.merk_model || '',
      tipe_kendaraan: v.tipe_kendaraan || '',
      warna: v.warna || '',
      tahun: v.tahun || '',
      no_rangka: v.no_rangka || '',
      no_mesin: v.no_mesin || '',
      masa_berlaku_stnk: v.masa_berlaku_stnk || '',
      catatan_visual: v.catatan_visual || '',
    });
    setShowVehicleForm(true);
  };
  const cancelVehicleForm = () => {
    setShowVehicleForm(false);
    setEditingVehicle(null);
    setVehicleForm(EMPTY_VEHICLE_FORM);
  };

  const saveVehicle = async () => {
    const plat = vehicleForm.no_plat.trim().toUpperCase();
    if (!plat) { alert('Plat nomor wajib diisi'); return; }
    setVehicleSaving(true);
    const payload = {
      no_plat: plat,
      nik_pemilik: penduduk.nik,
      merk_model: vehicleForm.merk_model.trim() || null,
      tipe_kendaraan: vehicleForm.tipe_kendaraan || null,
      warna: vehicleForm.warna.trim() || null,
      tahun: vehicleForm.tahun ? parseInt(vehicleForm.tahun, 10) || null : null,
      no_rangka: vehicleForm.no_rangka.trim() || null,
      no_mesin: vehicleForm.no_mesin.trim() || null,
      masa_berlaku_stnk: vehicleForm.masa_berlaku_stnk || null,
      catatan_visual: vehicleForm.catatan_visual.trim() || null,
    };
    let error = null;
    if (editingVehicle) {
      // EDIT: kalau plat berubah → delete lama + insert baru (PK change)
      if (editingVehicle.no_plat !== plat) {
        const { data: dup } = await supabase.from('kendaraan').select('no_plat').eq('no_plat', plat).maybeSingle();
        if (dup) error = { message: `Plat ${plat} sudah terdaftar` };
        else {
          const ins = await supabase.from('kendaraan').insert(payload);
          if (ins.error) error = ins.error;
          else {
            const del = await supabase.from('kendaraan').delete().eq('no_plat', editingVehicle.no_plat);
            if (del.error) error = del.error;
          }
        }
      } else {
        const upd = await supabase.from('kendaraan').update(payload).eq('no_plat', plat);
        error = upd.error;
      }
      if (!error) {
        setVehicles(prev => prev
          .filter(v => v.no_plat !== editingVehicle.no_plat)
          .concat(payload));
      }
    } else {
      // TAMBAH baru — cek dup plat dulu
      const { data: dup } = await supabase.from('kendaraan').select('no_plat').eq('no_plat', plat).maybeSingle();
      if (dup) error = { message: `Plat ${plat} sudah terdaftar di sistem` };
      else {
        const ins = await supabase.from('kendaraan').insert(payload);
        error = ins.error;
        if (!error) setVehicles(prev => [...prev, payload]);
      }
    }
    setVehicleSaving(false);
    if (error) {
      console.warn('[saveVehicle] fail:', error.message);
      alert(`Gagal simpan: ${error.message}`);
      return;
    }
    cancelVehicleForm();
  };

  const deleteVehicle = async (no_plat) => {
    if (!window.confirm(`Hapus kendaraan ${no_plat}?`)) return;
    const { data: deleted, error } = await supabase.from('kendaraan').delete().eq('no_plat', no_plat).select();
    if (error) {
      console.warn('[deleteVehicle] fail:', error.message);
      alert(`Gagal hapus: ${error.message}`);
      return;
    }
    if (!deleted || deleted.length === 0) {
      alert(`KES Data memblokir DELETE kendaraan ${no_plat} (RLS policy).\n\nHubungi admin untuk hapus manual.\nNo. Plat: ${no_plat}`);
      return;
    }
    setVehicles(prev => prev.filter(v => v.no_plat !== no_plat));
    if (editingVehicle?.no_plat === no_plat) cancelVehicleForm();
  };

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (isNew) {
      const nik = (form.nik || '').trim();
      if (!nik) { alert('NIK wajib diisi'); return; }
      if (!/^\d{16}$/.test(nik)) { alert('NIK harus 16 digit angka'); return; }
      if (!form.nama_lengkap?.trim()) { alert('Nama wajib diisi'); return; }
    }
    setSaving(true);
    const payload = { ...form };
    if (isNew) {
      payload.nik = (form.nik || '').trim();
      // strip empty strings → null untuk kolom optional supaya Supabase tidak komplain
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      const { data: dup } = await supabase.from('penduduk').select('nik').eq('nik', payload.nik).maybeSingle();
      if (dup) {
        setSaving(false);
        alert(`NIK ${payload.nik} sudah terdaftar`);
        return;
      }
    }
    // Pada UPDATE: jangan tulis balik kolom sistem/otomatis. `form` berasal dari
    // select('*') sehingga membawa face_embedding, last_seen, confidence_last, dll.
    // Menulisnya kembali bisa MENIMPA embedding yang baru diisi auto-heal sweeper
    // setelah panel dibuka (race → embedding hilang, badge balik ke PROSES).
    let writePayload = payload;
    if (!isNew) {
      const READONLY = ['face_embedding', 'face_embedding_light', 'last_seen', 'lokasi_last', 'confidence_last', 'created_at', 'nik'];
      writePayload = Object.fromEntries(Object.entries(payload).filter(([k]) => !READONLY.includes(k)));
      Object.keys(writePayload).forEach(k => { if (writePayload[k] === '') writePayload[k] = null; });
    }
    // .select() rowcount guard: Supabase REST mengembalikan data:null (BUKAN
    // error) bila ada kolom tak dikenal → tanpa guard ini UI klaim "Tersimpan"
    // padahal 0 baris ditulis (silent data-loss).
    const { data: wrote, error } = isNew
      ? await supabase.from('penduduk').insert(payload).select('nik')
      : await supabase.from('penduduk').update(writePayload).eq('nik', payload.nik).select('nik');
    setSaving(false);
    if (error) {
      console.warn('[handleSave] fail:', error.message);
      alert(`Gagal simpan: ${error.message}`);
      return;
    }
    if (!wrote || wrote.length === 0) {
      console.warn('[handleSave] 0 baris tertulis — kemungkinan kolom tidak dikenal / RLS');
      alert('Gagal simpan: tidak ada baris yang tertulis (cek kolom / izin).');
      return;
    }
    setSaved(true);
    onSaved(payload);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 shrink-0" style={{ borderBottom: '1px solid var(--border-dim)' }}>
        <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: isNew ? 'var(--color-green)' : 'var(--text-dim)' }}>
          {isNew ? '+ PENDUDUK BARU' : `${t('dc_edit')} PENDUDUK`}
        </p>
        <button onClick={onClose} className="rounded-lg p-1 hover:opacity-70 transition-all">
          <X size={14} color="var(--text-dim)" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto c-scroll p-5 space-y-4">
        {!isNew && (
          <PhotoGallery
            nik={form.nik}
            profileUrl={form.foto_url}
            onProfileChange={url => setForm(f => ({ ...f, foto_url: url }))}
          />
        )}
        {isNew && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl"
            style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.2)' }}>
            <AlertCircle size={11} color="var(--color-green)" className="shrink-0 mt-0.5" />
            <p className="text-[9px] font-mono" style={{ color: 'var(--text-dim)' }}>
              Isi data dasar lalu simpan. Setelah tersimpan, kamu bisa mengunggah foto wajah dan menambah kendaraan dari panel edit.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label={t('dc_nik')} value={form.nik} onChange={set('nik')} /></div>
          <div className="col-span-2"><Field label={t('dc_nama')} value={form.nama_lengkap} onChange={set('nama_lengkap')} /></div>
          <Field label={t("fld_tempat_lahir")} value={form.tempat_lahir} onChange={set('tempat_lahir')} />
          <Field label={t("fld_tgl_lahir")} value={form.tanggal_lahir} onChange={set('tanggal_lahir')} />
          <Field label={t('ni_gender')} value={form.jenis_kelamin} onChange={set('jenis_kelamin')} options={['LAKI-LAKI', 'PEREMPUAN']} />
          <Field label={t('ni_blood')} value={form.gol_darah} onChange={set('gol_darah')} options={['A', 'B', 'AB', 'O']} />
          <Field label={t('ni_religion')} value={form.agama} onChange={set('agama')} options={['Islam', 'Kristen', 'Katolik', 'Hindu', 'Budha', 'Konghucu']} />
          <Field label={t('ni_marital')} value={form.status_perkawinan} onChange={set('status_perkawinan')} options={['KAWIN', 'BELUM KAWIN', 'CERAI HIDUP', 'CERAI MATI']} />
          <Field label={t('ni_job')} value={form.pekerjaan} onChange={set('pekerjaan')} />
          <Field label={t('ni_citizen')} value={form.kewarganegaraan} onChange={set('kewarganegaraan')} options={['WNI', 'BELANDA', 'WNA']} />
        </div>

        <div className="space-y-3">
          <Field label={t("fld_alamat")} value={form.alamat_lengkap} onChange={set('alamat_lengkap')} />
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("fld_rt_rw")} value={form.rt_rw} onChange={set('rt_rw')} />
            <Field label={t("fld_kel")} value={form.kel_desa} onChange={set('kel_desa')} />
            <Field label={t("fld_kec")} value={form.kecamatan} onChange={set('kecamatan')} />
            <Field label={t('dc_kota')} value={form.kota_kabupaten} onChange={set('kota_kabupaten')} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('dc_status')} value={form.status_intelijen} onChange={set('status_intelijen')} options={['CLEAR', 'WATCHLIST', 'SUSPECT', 'WANTED']} optionLabel={(o) => getIntelLabel(o, lang)} />
          <Field label={t('ni_phys_note')} value={form.catatan_fisik} onChange={set('catatan_fisik')} />
        </div>

        {/* ── DOKUMEN IDENTITAS ──────────────────────────────────── */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FileText size={11} color="var(--color-cyan)" />
            <span className="text-[8px] font-black font-mono uppercase tracking-widest" style={{ color: 'var(--text-dim)' }}>
              Dokumen Identitas
            </span>
            {DOKUMEN_TYPES.filter(d => form[d.key]).length > 0 && (
              <span className="px-1.5 py-0.5 rounded font-black text-[7px] font-mono"
                style={{ background: 'rgba(0,212,255,0.12)', color: 'var(--color-cyan)' }}>
                {DOKUMEN_TYPES.filter(d => form[d.key]).length}/{DOKUMEN_TYPES.length}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {DOKUMEN_TYPES.map(d => (
              <DokumenSlot key={d.key}
                nik={form.nik}
                type={d.type}
                label={d.label}
                currentUrl={form[d.key]}
                onChange={url => {
                  // Update form state + persist immediately ke DB (jika nik sudah ada)
                  setForm(prev => ({ ...prev, [d.key]: url }));
                  if (form.nik && !isNew) {
                    supabase.from('penduduk').update({ [d.key]: url }).eq('nik', form.nik)
                      .then(({ error }) => { if (error) console.warn('[dokumen update]', error.message); });
                  }
                }} />
            ))}
          </div>
        </div>

        {/* ── KENDARAAN TERDAFTAR — CRUD ──────────────────────── */}
        {!isNew && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Car size={11} color="#ff6b2b" />
              <span className="text-[8px] font-black font-mono uppercase tracking-widest" style={{ color: 'var(--text-dim)' }}>
                Kendaraan Terdaftar
              </span>
              {vehicles.length > 0 && (
                <span className="px-1.5 py-0.5 rounded font-black text-[7px] font-mono"
                  style={{ background: 'rgba(255,107,43,0.15)', color: '#ff6b2b' }}>{vehicles.length}</span>
              )}
            </div>
            <button onClick={openAddVehicle}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[8px] font-mono hover:opacity-80 transition-opacity"
              style={{ background: 'rgba(0,212,255,0.10)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
              <Plus size={9} /> {t('btn_add_kendaraan')}
            </button>
          </div>

          {vehicleLoading && (
            <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>{t('loading_dots')}</p>
          )}

          {!vehicleLoading && vehicles.length === 0 && !showVehicleForm && (
            <p className="text-[8px] font-mono py-2" style={{ color: 'var(--text-mute)' }}>
              Belum ada kendaraan terdaftar
            </p>
          )}

          {vehicles.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {vehicles.map(v => {
                const status = stnkOk(v.masa_berlaku_stnk);
                const isEditing = editingVehicle?.no_plat === v.no_plat && showVehicleForm;
                return (
                  <div key={v.no_plat}
                    className="flex items-center justify-between px-3 py-2 rounded-xl transition-all"
                    style={{
                      background: isEditing ? 'rgba(0,212,255,0.05)' : 'var(--color-surface)',
                      border: `1px solid ${isEditing ? 'rgba(0,212,255,0.4)' : 'var(--border-dim)'}`,
                    }}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="px-2 py-0.5 rounded font-black font-mono text-[9px] shrink-0"
                        style={{ background: 'rgba(255,107,43,0.15)', border: '1px solid rgba(255,107,43,0.4)', color: '#ff6b2b' }}>
                        {v.no_plat}
                      </span>
                      <div className="flex flex-col min-w-0 leading-tight">
                        <span className="text-[9px] font-mono truncate" style={{ color: 'var(--text-hi)' }}>
                          {v.merk_model || '-'}
                        </span>
                        <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                          {[v.warna, v.tipe_kendaraan].filter(Boolean).join(' · ') || '-'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="px-1.5 py-0.5 rounded text-[7px] font-black font-mono"
                        style={{
                          background: status === true ? 'rgba(0,255,136,0.12)'
                                    : status === false ? 'rgba(255,45,85,0.12)'
                                    : 'rgba(128,128,128,0.10)',
                          color: status === true ? 'var(--color-green)'
                               : status === false ? '#ff2d55'
                               : 'var(--text-mute)',
                        }}
                        title={v.masa_berlaku_stnk ? fmtStnk(v.masa_berlaku_stnk) : (lang === 'id' ? 'Tidak ada data STNK' : 'No registration data')}>
                        {status === true ? 'STNK OK' : status === false ? 'STNK MATI' : 'STNK -'}
                      </span>
                      <button onClick={() => isEditing ? cancelVehicleForm() : openEditVehicle(v)}
                        className="p-1 rounded transition-colors"
                        title={isEditing ? 'Batal edit' : 'Edit kendaraan'}
                        style={{ color: isEditing ? 'var(--color-cyan)' : 'var(--text-dim)' }}>
                        <Edit2 size={11} />
                      </button>
                      <button onClick={() => deleteVehicle(v.no_plat)}
                        className="p-1 rounded transition-colors"
                        title={t('veh_title_delete')}
                        style={{ color: '#ff2d55' }}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Form tambah/edit */}
          {showVehicleForm && (
            <div className="rounded-xl p-3 mt-2"
              style={{ background: 'var(--color-surface)', border: '1px solid rgba(0,212,255,0.3)' }}>
              <p className="text-[8px] font-black font-mono uppercase tracking-widest mb-2" style={{ color: 'var(--color-cyan)' }}>
                {editingVehicle ? `EDIT — ${editingVehicle.no_plat}` : 'TAMBAH KENDARAAN'}
              </p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="col-span-2">
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>Plat Nomor *</label>
                  <input value={vehicleForm.no_plat}
                    onChange={e => setVehicleForm(f => ({ ...f, no_plat: e.target.value.toUpperCase() }))}
                    placeholder="B 1234 XY"
                    disabled={!!editingVehicle}
                    className="w-full px-2 py-1.5 rounded-lg text-xs font-mono outline-none"
                    style={{
                      background: 'var(--color-card)',
                      border: '1px solid var(--border-mid)',
                      color: editingVehicle ? 'var(--text-mute)' : 'var(--text-hi)',
                    }} />
                  {editingVehicle && (
                    <p className="text-[7px] mt-0.5" style={{ color: 'var(--text-mute)' }}>
                      Plat tidak bisa diubah — hapus dan buat baru jika perlu
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>Merk / Model</label>
                  <input value={vehicleForm.merk_model}
                    onChange={e => setVehicleForm(f => ({ ...f, merk_model: e.target.value }))}
                    placeholder="Toyota Avanza"
                    className="w-full px-2 py-1.5 rounded-lg text-xs outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }} />
                </div>
                <div>
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>Tipe</label>
                  <select value={vehicleForm.tipe_kendaraan}
                    onChange={e => setVehicleForm(f => ({ ...f, tipe_kendaraan: e.target.value }))}
                    className="w-full px-2 py-1.5 rounded-lg text-xs outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }}>
                    <option value="">{t('veh_select_type')}</option>
                    {['Sedan','SUV','MPV','Hatchback','Pickup','Truk','Bus','Motor','Lainnya'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>Warna</label>
                  <input value={vehicleForm.warna}
                    onChange={e => setVehicleForm(f => ({ ...f, warna: e.target.value }))}
                    placeholder="Putih"
                    className="w-full px-2 py-1.5 rounded-lg text-xs outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }} />
                </div>
                <div>
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>Tahun</label>
                  <input type="number" value={vehicleForm.tahun}
                    onChange={e => setVehicleForm(f => ({ ...f, tahun: e.target.value }))}
                    placeholder="2024"
                    min="1900" max="2100"
                    className="w-full px-2 py-1.5 rounded-lg text-xs font-mono outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }} />
                </div>
                <div>
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>Masa Berlaku STNK</label>
                  <input type="date" value={vehicleForm.masa_berlaku_stnk}
                    onChange={e => setVehicleForm(f => ({ ...f, masa_berlaku_stnk: e.target.value }))}
                    className="w-full px-2 py-1.5 rounded-lg text-xs font-mono outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }} />
                </div>
                <div>
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>No. Rangka</label>
                  <input value={vehicleForm.no_rangka}
                    onChange={e => setVehicleForm(f => ({ ...f, no_rangka: e.target.value.toUpperCase() }))}
                    placeholder="MHF..."
                    className="w-full px-2 py-1.5 rounded-lg text-xs font-mono outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }} />
                </div>
                <div>
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>No. Mesin</label>
                  <input value={vehicleForm.no_mesin}
                    onChange={e => setVehicleForm(f => ({ ...f, no_mesin: e.target.value.toUpperCase() }))}
                    placeholder="1NZ..."
                    className="w-full px-2 py-1.5 rounded-lg text-xs font-mono outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }} />
                </div>
                <div className="col-span-2">
                  <label className="block text-[7px] font-mono font-black uppercase tracking-wider mb-1"
                    style={{ color: 'var(--text-mute)' }}>Catatan Visual</label>
                  <input value={vehicleForm.catatan_visual}
                    onChange={e => setVehicleForm(f => ({ ...f, catatan_visual: e.target.value }))}
                    placeholder={t("cat_visual_ph")}
                    className="w-full px-2 py-1.5 rounded-lg text-xs outline-none"
                    style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', color: 'var(--text-hi)' }} />
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={saveVehicle} disabled={vehicleSaving}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[9px] font-black font-mono"
                  style={{
                    background: 'rgba(0,212,255,0.15)',
                    border: '1px solid rgba(0,212,255,0.4)',
                    color: 'var(--color-cyan)',
                    opacity: vehicleSaving ? 0.6 : 1,
                  }}>
                  {vehicleSaving
                    ? <><div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} /> {t('saving_dots')}</>
                    : <><Save size={11} /> {t('btn_save_kendaraan')}</>}
                </button>
                <button onClick={cancelVehicleForm} disabled={vehicleSaving}
                  className="px-3 py-2 rounded-xl text-[9px] font-mono"
                  style={{ border: '1px solid var(--border-dim)', color: 'var(--text-mute)' }}>
                  Batal
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      <div className="px-5 py-3 shrink-0 flex gap-2" style={{ borderTop: '1px solid var(--border-dim)' }}>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black transition-all"
          style={{ background: saved ? 'rgba(0,255,136,0.12)' : 'rgba(0,212,255,0.12)', border: `1px solid ${saved ? 'rgba(0,255,136,0.3)' : 'rgba(0,212,255,0.3)'}`, color: saved ? 'var(--color-green)' : 'var(--color-cyan)', opacity: saving ? 0.7 : 1 }}>
          {saved
            ? <><CheckCircle size={13} /> {t('dc_saved')}</>
            : saving
            ? <><div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} /> {t('dc_saving')}</>
            : <><Save size={13} /> {t('dc_save')}</>}
        </button>
        <button onClick={onClose}
          className="px-4 py-2.5 rounded-xl text-xs font-black transition-all hover:opacity-70"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
          {t('dc_cancel')}
        </button>
      </div>
    </div>
  );
};

const EMPTY_PENDUDUK_FORM = {
  nik: '',
  nama_lengkap: '',
  tempat_lahir: '',
  tanggal_lahir: '',
  jenis_kelamin: 'LAKI-LAKI',
  gol_darah: '',
  agama: 'Islam',
  status_perkawinan: 'BELUM KAWIN',
  pekerjaan: '',
  kewarganegaraan: 'WNI',
  alamat_lengkap: '',
  rt_rw: '',
  kel_desa: '',
  kecamatan: '',
  kota_kabupaten: '',
  status_intelijen: 'CLEAR',
  catatan_fisik: '',
  foto_url: null,
  face_embedding: null,
  dokumen_ktp_url: null,
  dokumen_passport_url: null,
  dokumen_akta_url: null,
  dokumen_kk_url: null,
};

const KendaraanFormModal = ({ pendudukList, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY_VEHICLE_FORM, nik_pemilik: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const filteredPenduduk = pendudukList.filter(p => {
    const q = search.toLowerCase();
    return !q || p.nama_lengkap?.toLowerCase().includes(q) || p.nik?.includes(q);
  }).slice(0, 50);

  const selectedPenduduk = pendudukList.find(p => p.nik === form.nik_pemilik);

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const save = async () => {
    const plat = form.no_plat.trim().toUpperCase();
    if (!plat) { alert('Plat nomor wajib diisi'); return; }
    if (!form.nik_pemilik) { alert('Pemilik (NIK) wajib dipilih'); return; }
    setSaving(true);
    const payload = {
      no_plat: plat,
      nik_pemilik: form.nik_pemilik,
      merk_model: form.merk_model.trim() || null,
      tipe_kendaraan: form.tipe_kendaraan || null,
      warna: form.warna.trim() || null,
      no_rangka: form.no_rangka.trim() || null,
      no_mesin: form.no_mesin.trim() || null,
      masa_berlaku_stnk: form.masa_berlaku_stnk || null,
      catatan_visual: form.catatan_visual.trim() || null,
    };
    const { data: dup } = await supabase.from('kendaraan').select('no_plat').eq('no_plat', plat).maybeSingle();
    if (dup) {
      setSaving(false);
      alert(`Plat ${plat} sudah terdaftar`);
      return;
    }
    const { error } = await supabase.from('kendaraan').insert(payload);
    setSaving(false);
    if (error) {
      console.warn('[KendaraanFormModal] insert:', error.message);
      alert(`Gagal simpan: ${error.message}`);
      return;
    }
    onSaved(payload);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(2,6,14,0.85)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-2xl rounded-3xl overflow-hidden flex flex-col"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', maxHeight: '88vh' }}>

        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border-dim)' }}>
          <div className="flex items-center gap-3">
            <Car size={15} color="#ff6b2b" />
            <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
              + KENDARAAN BARU
            </p>
          </div>
          <button onClick={onClose}><X size={13} color="var(--text-dim)" /></button>
        </div>

        <div className="flex-1 overflow-auto c-scroll p-5 space-y-4">
          {/* PEMILIK SELECTOR */}
          <div>
            <p className="text-[8px] uppercase tracking-widest mb-1.5 font-mono font-black" style={{ color: '#ff6b2b' }}>Pemilik (NIK) *</p>
            {selectedPenduduk ? (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.3)' }}>
                <div className="w-9 h-9 rounded-xl shrink-0 overflow-hidden flex items-center justify-center"
                  style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                  {selectedPenduduk.foto_url
                    ? <img src={selectedPenduduk.foto_url} alt="" className="w-full h-full object-cover" />
                    : <User size={16} color="var(--text-mute)" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate" style={{ color: 'var(--text-hi)' }}>{selectedPenduduk.nama_lengkap}</p>
                  <p className="text-[9px] font-mono truncate" style={{ color: 'var(--text-mute)' }}>{selectedPenduduk.nik}</p>
                </div>
                <button onClick={() => setForm(f => ({ ...f, nik_pemilik: '' }))}
                  className="text-[9px] font-mono px-2 py-1 rounded"
                  style={{ color: 'var(--text-mute)', border: '1px solid var(--border-dim)' }}>
                  Ganti
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-3 rounded-xl mb-2"
                  style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', height: 34 }}>
                  <Search size={11} color="var(--text-dim)" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search_name_nik')}
                    className="flex-1 bg-transparent outline-none text-xs" style={{ color: 'var(--text-mid)' }} />
                </div>
                <div className="rounded-xl overflow-hidden max-h-44 overflow-y-auto c-scroll"
                  style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                  {filteredPenduduk.length === 0 && (
                    <p className="text-[9px] font-mono py-3 text-center" style={{ color: 'var(--text-mute)' }}>
                      {search
                        ? (lang === 'id' ? 'Tidak ada penduduk cocok' : 'No matching residents')
                        : (lang === 'id' ? 'Mulai ketik nama atau NIK...' : 'Start typing name or ID...')}
                    </p>
                  )}
                  {filteredPenduduk.map(p => (
                    <button key={p.nik}
                      onClick={() => { setForm(f => ({ ...f, nik_pemilik: p.nik })); setSearch(''); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5"
                      style={{ borderBottom: '1px solid var(--border-lo)' }}>
                      <div className="w-7 h-7 rounded-lg shrink-0 overflow-hidden flex items-center justify-center"
                        style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>
                        {p.foto_url
                          ? <img src={p.foto_url} alt="" className="w-full h-full object-cover" />
                          : <User size={11} color="var(--text-mute)" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold truncate" style={{ color: 'var(--text-hi)' }}>{p.nama_lengkap}</p>
                        <p className="text-[8px] font-mono truncate" style={{ color: 'var(--text-mute)' }}>{p.nik}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* FORM KENDARAAN */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Field label={t("fld_no_plat")} value={form.no_plat}
                onChange={v => set('no_plat')(v.toUpperCase())} />
            </div>
            <Field label={t("fld_merk_model")} value={form.merk_model} onChange={set('merk_model')} />
            <Field label={t("fld_tipe")} value={form.tipe_kendaraan} onChange={set('tipe_kendaraan')}
              options={['', 'Sedan', 'SUV', 'MPV', 'Hatchback', 'Pickup', 'Truk', 'Bus', 'Motor', 'Lainnya']} />
            <Field label={t("fld_warna")} value={form.warna} onChange={set('warna')} />
            <Field label={t("fld_masa_stnk")} type="date" value={form.masa_berlaku_stnk} onChange={set('masa_berlaku_stnk')} />
            <Field label={t("fld_no_rangka")} value={form.no_rangka} onChange={v => set('no_rangka')(v.toUpperCase())} />
            <Field label={t("fld_no_mesin")} value={form.no_mesin} onChange={v => set('no_mesin')(v.toUpperCase())} />
            <div className="col-span-2">
              <Field label={t("fld_catatan_visual")} value={form.catatan_visual} onChange={set('catatan_visual')} />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 flex gap-2 shrink-0" style={{ borderTop: '1px solid var(--border-dim)' }}>
          <button onClick={save} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black"
            style={{ background: 'rgba(255,107,43,0.12)', border: '1px solid rgba(255,107,43,0.4)', color: '#ff6b2b', opacity: saving ? 0.7 : 1 }}>
            {saving
              ? <><div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: '#ff6b2b', borderTopColor: 'transparent' }} /> {t('saving_dots')}</>
              : <><Save size={13} /> {t('btn_save_kendaraan')}</>}
          </button>
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-xs font-black"
            style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
            Batal
          </button>
        </div>
      </div>
    </div>
  );
};


const PendudukTab = () => {
  const { t } = useApp();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showKendaraanModal, setShowKendaraanModal] = useState(false);
  const [hfFaceCount, setHfFaceCount] = useState(null);
  const [healProgress, setHealProgress] = useState(null); // { current, total, nama } | null
  const healingRef = useRef(false);

  useEffect(() => {
    let alive = true;
    supabase.from('penduduk').select('*').order('nama_lengkap').then(({ data, error }) => {
      if (!alive) return;
      if (error) console.warn('[PendudukTab] load:', error.message);
      setList(data || []);
    });
    return () => { alive = false; };
  }, []);

  // Poll HF face_db count untuk sync indicator (Supabase emb count vs HF face_db count)
  useEffect(() => {
    let alive = true;
    const fetchHF = async () => {
      try {
        // Lewat proxy: Space privat, /health langsung dari browser 404 (panel menampilkan "HF: --").
        const r = await fetch('/api/hf-health', { signal: AbortSignal.timeout(8000) });
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setHfFaceCount(j.face_db_count ?? null);
      } catch { /* silent — HF mungkin sleeping */ }
    };
    fetchHF();
    const intv = setInterval(fetchHF, 60_000);
    return () => { alive = false; clearInterval(intv); };
  }, []);

  const supaEmbCount = list.filter(p => p.face_embedding).length;
  const supaFotoCount = list.filter(p => p.foto_url).length;
  // Gap = punya foto tapi belum ada embedding. Auto-heal server-side (pg_cron)
  // akan mengisinya otomatis; indikator ini supaya operator tidak pernah buta.
  const embGap = Math.max(0, supaFotoCount - supaEmbCount);
  const inSync = hfFaceCount !== null && hfFaceCount === supaEmbCount;
  // syncStatus: idle saat belum ada polling, syncing saat HF unreachable transient,
  // success bila inSync, error bila ada divergensi count antara Supabase emb dan HF
  const syncStatus = hfFaceCount === null ? 'idle' : inSync ? 'success' : 'error';

  const filtered = list.filter(p => {
    const q = search.toLowerCase();
    return !q || p.nama_lengkap?.toLowerCase().includes(q) || p.nik?.includes(q) || p.kota_kabupaten?.toLowerCase().includes(q);
  });

  const handleHeal = useCallback(async () => {
    if (healingRef.current) return;
    healingRef.current = true;
    setHealProgress({ current: 0, total: 0, nama: '' });
    try {
      await syncMissingEmbeddings(supabase, ({ current, total, nama }) => {
        setHealProgress({ current, total, nama });
      });
      // Refresh list after heal
      const { data } = await supabase.from('penduduk').select('*').order('nama_lengkap');
      if (data) setList(data);
    } finally {
      healingRef.current = false;
      setHealProgress(null);
    }
  }, []);

  const handleSaved = useCallback((updated) => {
    setList(prev => prev.map(p => p.nik === updated.nik ? updated : p));
    setSelected(updated);
  }, []);

  const handleCreated = useCallback((created) => {
    setList(prev => {
      const without = prev.filter(p => p.nik !== created.nik);
      return [...without, created].sort((a, b) =>
        (a.nama_lengkap || '').localeCompare(b.nama_lengkap || ''));
    });
    setCreating(false);
    setSelected(created);
  }, []);

  const startCreate = () => {
    setSelected(null);
    setCreating(true);
  };

  return (
    <div className="flex flex-1 min-h-0 gap-0 overflow-hidden">
      <div className="w-72 shrink-0 flex flex-col border-r overflow-hidden" style={{ borderColor: 'var(--border-dim)' }}>
        <div className="px-3 py-3 shrink-0 space-y-2" style={{ borderBottom: '1px solid var(--border-dim)' }}>
          {/* SYNC INDICATOR — Supabase ↔ HF face_db status */}
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl"
            style={{
              background: 'var(--color-surface)',
              border: `1px solid ${hfFaceCount === null ? 'var(--border-dim)' : inSync ? 'rgba(0,255,136,0.25)' : 'rgba(255,107,43,0.30)'}`,
            }}>
            <div className="w-1.5 h-1.5 rounded-full"
              style={{
                background: hfFaceCount === null ? 'var(--text-mute)' : inSync ? 'var(--color-green)' : '#ff6b2b',
                boxShadow: hfFaceCount !== null && inSync ? '0 0 6px var(--color-green)' : 'none',
              }} />
            <div className="flex-1 min-w-0">
              <p className="text-[7px] font-black uppercase tracking-widest font-mono"
                style={{ color: 'var(--text-mute)' }}>
                {t('sync_db_ai')} · {syncStatus === 'idle' ? t('sync_idle') : syncStatus === 'success' ? t('sync_done') : t('sync_error')}
              </p>
              <p className="text-[8px] font-mono leading-tight" style={{ color: 'var(--text-dim)' }}>
                {list.length} {t('label_penduduk')} · {supaFotoCount} {t('label_foto')} · {supaEmbCount} {t('label_emb')}
                {hfFaceCount !== null && (
                  <span style={{ color: inSync ? 'var(--color-green)' : '#ff6b2b' }}>
                    {' '}· HF: {hfFaceCount}{!inSync && ' ⚠'}
                  </span>
                )}
                {hfFaceCount === null && <span style={{ color: 'var(--text-mute)' }}> · HF: --</span>}
              </p>
              {embGap > 0 && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  {healProgress ? (
                    <p className="text-[8px] font-mono font-bold leading-tight" style={{ color: '#ffd60a' }}>
                      Memproses {healProgress.current}/{healProgress.total}
                      {healProgress.nama ? ` — ${healProgress.nama}` : ''}...
                    </p>
                  ) : (
                    <>
                      <p className="text-[8px] font-mono font-bold leading-tight" style={{ color: '#ffd60a' }}>
                        {embGap} foto belum diproses
                      </p>
                      <button
                        onClick={handleHeal}
                        className="text-[7px] font-black font-mono px-1.5 py-0.5 rounded transition-all hover:opacity-80"
                        style={{ background: 'rgba(255,214,10,0.15)', border: '1px solid rgba(255,214,10,0.4)', color: '#ffd60a' }}>
                        Perbaiki Sekarang
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 rounded-xl" style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', height: 36 }}>
            <Search size={12} color="var(--text-dim)" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('dc_search')}
              className="flex-1 bg-transparent outline-none text-xs" style={{ color: 'var(--text-mid)' }} />
            {search && <button onClick={() => setSearch('')}><X size={11} color="var(--text-dim)" /></button>}
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={startCreate}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-xl text-[9px] font-black font-mono transition-all hover:opacity-80"
              style={{ background: 'rgba(0,255,136,0.10)', border: '1px solid rgba(0,255,136,0.3)', color: 'var(--color-green)' }}>
              <Plus size={9} /> {t('btn_penduduk')}
            </button>
            <button onClick={() => setShowKendaraanModal(true)}
              disabled={list.length === 0}
              title={list.length === 0 ? t('add_penduduk_first') : t('add_kendaraan_new')}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-xl text-[9px] font-black font-mono transition-all hover:opacity-80"
              style={{ background: 'rgba(255,107,43,0.10)', border: '1px solid rgba(255,107,43,0.3)', color: '#ff6b2b', opacity: list.length === 0 ? 0.4 : 1 }}>
              <Plus size={9} /> {t('btn_kendaraan')}
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto c-scroll">
          {filtered.map(p => (
            <PendudukRow key={p.nik} p={p} selected={selected} onSelect={(x) => { setCreating(false); setSelected(x); }} />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-hidden" style={{ background: 'var(--color-bg)' }}>
        {creating
          ? <EditPanel key="__new__" penduduk={EMPTY_PENDUDUK_FORM} isNew
              onSaved={handleCreated} onClose={() => setCreating(false)} />
          : selected
          ? <EditPanel key={selected.nik} penduduk={selected} onSaved={handleSaved} onClose={() => setSelected(null)} />
          : <div className="h-full flex flex-col items-center justify-center gap-3">
              <Edit2 size={32} color="rgba(0,212,255,0.12)" strokeWidth={1} />
              <p className="text-sm" style={{ color: 'var(--text-mute)' }}>{t('dc_no_select')}</p>
            </div>}
      </div>

      {showKendaraanModal && (
        <KendaraanFormModal
          pendudukList={list}
          onClose={() => setShowKendaraanModal(false)}
          onSaved={() => { /* realtime channel akan refresh kendaraan global */ }}
        />
      )}
    </div>
  );
};

// ─── System Log Tab (C-004) ────────────────────────────────────────────────────
const SystemLogTab = () => {
  const { lang } = useApp();
  const [logs, setLogs] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('horus_system_logs') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    const id = setInterval(() => {
      try { setLogs(JSON.parse(sessionStorage.getItem('horus_system_logs') || '[]')); } catch {}
    }, 2000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex flex-col h-full p-4 gap-3">
      <div className="flex items-center justify-between shrink-0">
        <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--color-cyan)' }}>
          SYSTEM LOG
        </p>
        <span className="text-[8px] font-mono px-2 py-0.5 rounded"
          style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)' }}>
          {logs.length} {lang === 'id' ? 'entri' : 'entries'} · auto-refresh 2s
        </span>
      </div>
      <div className="flex-1 overflow-y-auto c-scroll rounded-xl p-3 space-y-1"
        style={{ background: 'var(--color-surface)', border: '1px solid var(--border-lo)' }}>
        {logs.length === 0 ? (
          <p className="text-[9px] font-mono text-center py-6" style={{ color: 'var(--text-mute)' }}>
            {lang === 'id'
              ? 'Belum ada log. Buka Vision dan aktifkan CCTV / scan untuk generate log.'
              : 'No logs yet. Open Vision and activate CCTV / scan to generate logs.'}
          </p>
        ) : (
          [...logs].reverse().map((l, i) => (
            <p key={i} className="text-[9px] font-mono leading-relaxed" style={{ color: l.color }}>
              <span style={{ color: 'var(--text-mute)', marginRight: 6 }}>[{l.time}]</span>
              {l.msg}
            </p>
          ))
        )}
      </div>
    </div>
  );
};

// ─── CCTV Tab ─────────────────────────────────────────────────────────────────

const BatchVideoUploadModal = ({ kameras, onClose, onDone }) => {
  const { t, lang } = useApp();
  const [items, setItems]   = useState([]);
  const [running, setRunning] = useState(false);
  const [done, setDone]     = useState(false);
  const fileRef = useRef();

  // ESC untuk close (selama tidak sedang upload supaya tidak interrupt)
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !running) onClose?.(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [running, onClose]);

  const handleFiles = (files) => {
    const arr = [];
    const rejected = [];
    for (const f of Array.from(files)) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      const isVideo = VIDEO_EXT_WHITELIST.includes(ext);
      const isImage = IMAGE_EXT_WHITELIST.includes(ext);
      if (!isVideo && !isImage) {
        rejected.push(`${f.name} (format .${ext} tidak didukung)`);
        continue;
      }
      const cap = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      if (f.size > cap) {
        rejected.push(`${f.name} (>${(cap / 1024 / 1024).toFixed(0)} MB)`);
        continue;
      }
      arr.push({ file: f, kind: isVideo ? 'video' : 'image' });
    }
    if (rejected.length) alert('File ditolak:\n• ' + rejected.join('\n• '));
    if (!arr.length) return;

    // 1 file = 1 kamera baru. Existing kameras tidak diubah, file lama tidak ditimpa.
    const startNum = kameras.length + 1;
    setItems(arr.map((entry, i) => {
      const baseName = entry.file.name.replace(/\.[^.]+$/, '').slice(0, 16);
      const kameraNamaTarget = `CCTV-${String(startNum + i).padStart(3, '0')} (${baseName})`;
      return {
        file: entry.file,
        kind: entry.kind,
        kameraNamaTarget,
        reason: 'kamera baru',
        status: 'pending',
      };
    }));
  };

  const run = async () => {
    if (!items.length || running) return;
    setRunning(true);

    for (let i = 0; i < items.length; i++) {
      const { file, kameraNamaTarget, kind } = items[i];
      try {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const isImage = kind === 'image';
        if (!isImage && !VIDEO_EXT_WHITELIST.includes(ext)) {
          throw new Error(`format .${ext} tidak didukung`);
        }

        // 1. Insert kamera baru — id punya random suffix supaya tidak collision
        setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'uploading' } : it));
        const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
        const newId = `CAM-${Date.now().toString(36).toUpperCase()}-${rand}`;
        const { data: newKam, error: kamErr } = await supabase.from('kamera').insert({
          id: newId,
          nama: kameraNamaTarget,
          lokasi: 'Auto-imported',
          status: 'ONLINE',
          playlist: [],
          total_videos: 0,
        }).select().single();
        if (kamErr) throw kamErr;

        // 2. Upload file ke storage di folder kamera baru
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${newId}/${Date.now()}_${safeName}`;
        const { error } = await supabase.storage
          .from('cctv-videos').upload(path, file, { upsert: false, contentType: file.type });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('cctv-videos').getPublicUrl(path);
        const url = urlData.publicUrl;

        // 3. Update playlist kamera baru dengan 1 file ini
        const playlist = [{
          url, nama: file.name, size: file.size,
          kind: isImage ? 'image' : 'video',
          uploaded_at: new Date().toISOString(),
        }];
        const { error: updErr } = await supabase.from('kamera').update({
          playlist, total_videos: 1,
        }).eq('id', newId);
        if (updErr) throw updErr;

        setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'done', kameraId: newId } : it));
      } catch (e) {
        console.warn('[batch upload] fail:', e?.message || e);
        setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'error', err: e?.message || 'gagal' } : it));
      }
    }

    setRunning(false);
    setDone(true);
    const { data, error } = await supabase.from('kamera').select('*').order('nama');
    if (error) console.warn('[batch upload] reload kamera:', error.message);
    if (data) onDone(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(2,6,14,0.78)', backdropFilter: 'blur(6px)' }}
      onClick={() => { if (!running) onClose?.(); }}>
      <div className="w-full max-w-lg rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: 'var(--color-card)',
          border: '1px solid rgba(124,77,255,0.4)',
          boxShadow: '0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,77,255,0.15)',
          maxHeight: '80vh',
        }}
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: '1px solid var(--border-dim)' }}>
          <div className="flex items-center gap-2">
            <UploadCloud size={14} color="#7c4dff" />
            <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
              {lang === 'id' ? 'Unggah Rekaman CCTV' : 'Upload CCTV Recordings'}
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:opacity-70" aria-label="Close">
            <X size={13} color="var(--text-dim)" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8">
            <div onClick={() => fileRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-4 rounded-2xl cursor-pointer transition-all hover:opacity-90"
              style={{ height: 180, border: '2px dashed var(--border-mid)', background: 'var(--color-surface)' }}>
              <UploadCloud size={44} color="rgba(124,77,255,0.3)" strokeWidth={1} />
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: 'var(--text-dim)' }}>{t('sel_video_or_photo')}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-mute)' }}>
                  {lang === 'id'
                    ? 'Setiap file akan jadi kamera CCTV baru. Video (.mp4 / .mov / .mkv …) atau gambar (.jpg / .png / .webp).'
                    : 'Each file becomes a new CCTV camera. Video (.mp4 / .mov / .mkv …) or image (.jpg / .png / .webp).'}
                </p>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="video/*,image/*" multiple className="hidden"
              onChange={e => handleFiles(e.target.files)} />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto c-scroll p-4 space-y-1.5">
              {items.map((item, i) => {
                const isBusy = item.status === 'uploading';
                const busyColor = '#7c4dff';
                const iconBg = item.status === 'done' ? 'rgba(0,255,136,0.15)'
                  : item.status === 'error' ? 'rgba(255,45,85,0.15)'
                  : item.status === 'uploading' ? 'rgba(124,77,255,0.15)'
                  : 'var(--color-bg)';
                const labelColor = item.status === 'done' ? 'var(--color-green)'
                  : item.status === 'error' ? '#ff2d55'
                  : item.status === 'uploading' ? '#7c4dff'
                  : 'var(--text-mute)';
                const glyph = item.status === 'done' ? '✅'
                  : item.status === 'error' ? '❌'
                  : item.status === 'uploading' ? '⏳' : '⌛';
                return (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl"
                  style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                  <div className="w-6 h-6 rounded-lg shrink-0 flex items-center justify-center"
                    style={{ background: iconBg }}>
                    {item.status === 'done'    ? <CheckCircle size={12} color="var(--color-green)" />
                     : item.status === 'error' ? <X size={12} color="#ff2d55" />
                     : isBusy                  ? <div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: busyColor, borderTopColor: 'transparent' }} />
                     : <Video size={10} color="var(--text-mute)" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-bold truncate" style={{ color: 'var(--text-hi)' }}>{item.file.name}</p>
                    <p className="text-[8px] truncate" style={{ color: 'var(--text-mute)' }}>
                      → {item.kameraNamaTarget || item.kamera?.nama || 'kamera baru'}
                    </p>
                  </div>
                  <span className="text-[9px] font-black shrink-0 font-mono"
                    style={{ color: labelColor }}>
                    {glyph}
                  </span>
                </div>
                );
              })}
            </div>
            <div className="px-5 py-4 flex gap-3 shrink-0" style={{ borderTop: '1px solid var(--border-dim)' }}>
              {done ? (
                <button onClick={onClose}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black"
                  style={{ background: 'rgba(0,255,136,0.12)', border: '1px solid rgba(0,255,136,0.3)', color: 'var(--color-green)' }}>
                  <CheckCircle size={13} /> {lang === 'id' ? 'Selesai' : 'Done'}
                </button>
              ) : (
                <>
                  <button onClick={run} disabled={running}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black"
                    style={{ background: running ? 'rgba(124,77,255,0.07)' : 'rgba(124,77,255,0.12)', border: '1px solid rgba(124,77,255,0.3)', color: '#7c4dff', opacity: running ? 0.8 : 1 }}>
                    {running
                      ? <><div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: '#7c4dff', borderTopColor: 'transparent' }} /> {lang === 'id' ? 'Mengunggah...' : 'Uploading...'}</>
                      : <><UploadCloud size={13} /> {lang === 'id' ? `Unggah ${items.length} File` : `Upload ${items.length} Files`}</>}
                  </button>
                  {!running && (
                    <button onClick={() => setItems([])}
                      className="px-4 py-2.5 rounded-xl text-xs font-black"
                      style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
                      {lang === 'id' ? 'Ulang' : 'Reset'}
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// KameraCard — pure DB-management view. NO embedded player.
// Player lives in NeoVision Live Feed; this is the Google-Drive-style admin card.
const KameraCard = ({ kamera, onStatusToggle, onUpdate, onDelete, onOpenInNeoVision }) => {
  const { t } = useApp();
  const playlist = kamera.playlist || [];
  const totalSize = playlist.reduce((s, v) => s + (v.size || 0), 0);

  const [editNama,   setEditNama]   = useState(false);
  const [editLokasi, setEditLokasi] = useState(false);
  const [namaVal,    setNamaVal]    = useState(kamera.nama);
  const [lokasiVal,  setLokasiVal]  = useState(kamera.lokasi || '');
  const [editStream, setEditStream] = useState(false);
  const [streamVal,  setStreamVal]  = useState(kamera.stream_url || '');
  const [savedField, setSavedField] = useState(null);
  const [showAllFiles, setShowAllFiles] = useState(false);

  const online = kamera.status === 'ONLINE';
  const sc = online
    ? { color: 'var(--color-green)', bg: 'rgba(0,255,136,0.10)' }
    : { color: 'var(--text-mute)', bg: 'var(--color-surface)' };

  const flash = (field) => {
    setSavedField(field);
    setTimeout(() => setSavedField(null), 2000);
  };

  const saveNama = async () => {
    const v = namaVal.trim();
    if (!v || v === kamera.nama) { setEditNama(false); return; }
    const { error } = await supabase.from('kamera').update({ nama: v }).eq('id', kamera.id);
    setEditNama(false);
    if (error) {
      console.warn('[saveNama] fail:', error.message);
      alert(`Gagal simpan nama: ${error.message}`);
      setNamaVal(kamera.nama);
      return;
    }
    flash('nama');
    onUpdate({ ...kamera, nama: v });
  };

  const saveLokasi = async () => {
    const v = lokasiVal.trim();
    if (v === (kamera.lokasi || '')) { setEditLokasi(false); return; }
    const { error } = await supabase.from('kamera').update({ lokasi: v }).eq('id', kamera.id);
    setEditLokasi(false);
    if (error) {
      console.warn('[saveLokasi] fail:', error.message);
      alert(`Gagal simpan lokasi: ${error.message}`);
      setLokasiVal(kamera.lokasi || '');
      return;
    }
    flash('lokasi');
    onUpdate({ ...kamera, lokasi: v });
  };

  const saveStream = async () => {
    const url = streamVal.trim() || null;
    if (url === (kamera.stream_url || null)) { setEditStream(false); return; }
    const { error } = await supabase.from('kamera').update({ stream_url: url }).eq('id', kamera.id);
    setEditStream(false);
    if (error) {
      console.warn('[saveStream] fail:', error.message);
      alert(`Gagal simpan URL stream: ${error.message}`);
      setStreamVal(kamera.stream_url || '');
      return;
    }
    flash('stream');
    onUpdate({ ...kamera, stream_url: url });
  };

  const deleteFile = async (videoUrl) => {
    // 1. Hapus dari storage dulu — kalau gagal, jangan lanjut update playlist
    const path = safeStoragePath(videoUrl, 'cctv-videos');
    if (path) {
      const { data: removed, error } = await supabase.storage.from('cctv-videos').remove([path]);
      if (error) {
        alert(`Gagal hapus file dari storage: ${error.message}`);
        return;
      }
      if (!removed || removed.length === 0) {
        console.warn('[deleteFile] storage remove returned empty — RLS atau path salah:', path);
      }
    }
    // 2. Update playlist di DB — pakai .select() untuk verifikasi row affected
    const newPL = playlist.filter(v => v.url !== videoUrl);
    const { data: updated, error } = await supabase.from('kamera')
      .update({ playlist: newPL, total_videos: newPL.length })
      .eq('id', kamera.id)
      .select();
    if (error) {
      alert(`Gagal hapus rekaman: ${error.message}`);
      return;
    }
    if (!updated || updated.length === 0) {
      // RLS silently blocked — UPDATE returned 0 rows
      alert(`RLS blokir update kamera. Hubungi admin untuk konfigurasi policy.`);
      return;
    }
    onUpdate({ ...kamera, playlist: newPL, total_videos: newPL.length });
  };

  const handleDeleteCamera = async () => {
    if (!window.confirm(`Hapus kamera "${kamera.nama}"? Semua rekaman akan dihapus.`)) return;
    // 1. Hapus file storage — bukan blocker, lanjut walau gagal partial
    const paths = playlist.map(v => safeStoragePath(v.url, 'cctv-videos')).filter(Boolean);
    if (paths.length) {
      const { error } = await supabase.storage.from('cctv-videos').remove(paths);
      if (error) console.warn('[deleteCamera] storage cleanup:', error.message);
    }
    // 2. Hapus row kamera — pakai .select() untuk verifikasi row beneran ke-delete
    const { data: deleted, error } = await supabase
      .from('kamera')
      .delete()
      .eq('id', kamera.id)
      .select();
    if (error) {
      alert(`Gagal hapus kamera: ${error.message}`);
      return;
    }
    if (!deleted || deleted.length === 0) {
      // RLS silently blocked — DELETE returned 0 rows. Ini yang bikin
      // 'klik delete tapi setelah refresh masih ada' — UI optimistic update,
      // tapi DB tidak pernah berubah.
      alert(`KES Data memblokir DELETE kamera (RLS policy).\n\nKamera tidak terhapus dari database.\nHubungi admin untuk hapus manual.\n\nKamera ID: ${kamera.id}`);
      return;
    }
    onDelete(kamera.id);
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/1048576).toFixed(1)} MB`;
  };
  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' });
    } catch { return ''; }
  };
  const visibleFiles = showAllFiles ? playlist : playlist.slice(0, 3);

  return (
    <div className="rounded-2xl p-4 flex flex-col gap-3 transition-all hover:border-cyan-500"
      style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>

      {/* Section 1 — Header: status badge + nama + delete */}
      <div className="flex items-start gap-2">
        <button onClick={() => onStatusToggle(kamera)}
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[8px] font-black shrink-0 transition-all hover:opacity-80"
          style={{ background: sc.bg, border: `1px solid ${sc.color}33`, color: sc.color }}
          title={t('cam_title_toggle')}>
          {online ? <Wifi size={9} /> : <WifiOff size={9} />}
          {online ? 'ONLINE' : 'OFFLINE'}
        </button>
        <div className="flex-1 min-w-0">
          {editNama ? (
            <input value={namaVal} onChange={e => setNamaVal(e.target.value)}
              onBlur={saveNama}
              onKeyDown={e => {
                if (e.key === 'Enter') saveNama();
                else if (e.key === 'Escape') { setNamaVal(kamera.nama); setEditNama(false); }
              }}
              autoFocus
              className="bg-transparent border-b outline-none text-sm font-black w-full"
              style={{ color: 'var(--text-hi)', borderColor: 'var(--color-cyan)' }} />
          ) : (
            <p onClick={() => setEditNama(true)}
              className="text-sm font-black cursor-pointer hover:opacity-70 transition-opacity truncate flex items-center gap-1"
              style={{ color: 'var(--text-hi)' }} title={t('cam_title_edit_name')}>
              {namaVal}<Edit2 size={9} className="opacity-30" />
              {savedField === 'nama' && <CheckCircle size={10} color="var(--color-green)" />}
            </p>
          )}
        </div>
        <button onClick={handleDeleteCamera} title={t('cam_title_delete')}
          className="flex items-center justify-center rounded-lg shrink-0 transition-all hover:opacity-80"
          style={{ width: 24, height: 24, background: 'rgba(255,45,85,0.10)', border: '1px solid rgba(255,45,85,0.25)' }}>
          <Trash2 size={11} color="#ff2d55" />
        </button>
      </div>

      {/* Section 2 — Lokasi inline-edit */}
      <div className="flex items-center gap-1.5">
        <MapPin size={10} color="var(--text-mute)" className="shrink-0" />
        {editLokasi ? (
          <input value={lokasiVal} onChange={e => setLokasiVal(e.target.value)}
            onBlur={saveLokasi}
            onKeyDown={e => {
              if (e.key === 'Enter') saveLokasi();
              else if (e.key === 'Escape') { setLokasiVal(kamera.lokasi || ''); setEditLokasi(false); }
            }}
            autoFocus
            className="bg-transparent border-b outline-none text-[10px] flex-1"
            style={{ color: 'var(--text-mid)', borderColor: 'var(--color-cyan)' }} />
        ) : (
          <p onClick={() => setEditLokasi(true)}
            className="text-[10px] cursor-pointer hover:opacity-70 transition-opacity truncate flex-1 flex items-center gap-1"
            style={{ color: 'var(--text-mute)' }} title={t('cam_title_edit_loc')}>
            {lokasiVal || '—'} <Edit2 size={8} className="opacity-30" />
            {savedField === 'lokasi' && <CheckCircle size={8} color="var(--color-green)" />}
          </p>
        )}
      </div>

      {/* Section 3 — Rekaman list (file rows, no player) */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <p className="text-[8px] uppercase tracking-widest font-mono" style={{ color: 'var(--text-mute)' }}>
            Rekaman {playlist.length} file
          </p>
          {totalSize > 0 && (
            <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>{fmtSize(totalSize)}</p>
          )}
        </div>
        {playlist.length === 0 ? (
          <p className="text-[10px] italic" style={{ color: 'var(--text-mute)' }}>{t('rec_empty')}</p>
        ) : (
          <div className="flex flex-col gap-1">
            {visibleFiles.map((v, idx) => {
              const isImage = v.kind === 'image';
              return (
                <div key={v.url || idx}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors hover:bg-opacity-50"
                  style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                  {isImage
                    ? <ImageIcon size={11} color="#7c4dff" className="shrink-0" />
                    : <Video size={11} color="#ff6b2b" className="shrink-0" />}
                  <span className="text-[9px] font-mono truncate flex-1" style={{ color: 'var(--text-mid)' }} title={v.nama}>
                    {(v.nama || `file_${idx+1}`).slice(0, 24)}{(v.nama || '').length > 24 ? '…' : ''}
                  </span>
                  <span className="text-[8px] font-mono shrink-0" style={{ color: 'var(--text-mute)' }}>
                    {fmtSize(v.size)}
                  </span>
                  {v.uploaded_at && (
                    <span className="text-[8px] font-mono shrink-0 hidden md:inline" style={{ color: 'var(--text-mute)' }}>
                      {fmtDate(v.uploaded_at)}
                    </span>
                  )}
                  <button onClick={() => deleteFile(v.url)} title={t('rec_title_delete')}
                    className="flex items-center justify-center rounded transition-all opacity-50 hover:opacity-100 shrink-0"
                    style={{ width: 16, height: 16, background: 'rgba(255,45,85,0.10)' }}>
                    <X size={9} color="#ff2d55" />
                  </button>
                </div>
              );
            })}
            {playlist.length > 3 && (
              <button onClick={() => setShowAllFiles(v => !v)}
                className="text-[9px] font-bold mt-0.5 hover:opacity-80"
                style={{ color: 'var(--color-cyan)' }}>
                {showAllFiles ? '— Tampilkan sebagian' : `+ ${playlist.length - 3} lagi`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Section 4 — Stream URL */}
      <div className="flex flex-col gap-1.5">
        <p className="text-[8px] uppercase tracking-widest font-mono" style={{ color: 'var(--text-mute)' }}>
          URL Stream (HLS / WebRTC / RTSP)
        </p>
        <p className="text-[8px] font-mono leading-tight" style={{ color: 'var(--text-mute)' }}>
          HLS (.m3u8) &amp; WebRTC (WHEP) main langsung. RTSP perlu gateway (go2rtc / MediaMTX).
        </p>
        {editStream ? (
          <div className="flex gap-1">
            <input value={streamVal} onChange={e => setStreamVal(e.target.value)} autoFocus
              placeholder="https://host/stream.m3u8  •  webrtc://host/api/webrtc?src=cam  •  rtsp://..."
              onKeyDown={e => {
                if (e.key === 'Enter') saveStream();
                else if (e.key === 'Escape') { setStreamVal(kamera.stream_url || ''); setEditStream(false); }
              }}
              className="flex-1 px-2 py-1.5 rounded-lg text-[9px] font-mono outline-none min-w-0"
              style={{ background: 'var(--color-surface)', border: '1px solid rgba(0,212,255,0.35)', color: 'var(--text-mid)' }} />
            <button onClick={saveStream}
              className="px-2 py-1 rounded-lg text-[9px] font-black shrink-0"
              style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>{t('save')}</button>
            <button onClick={() => { setStreamVal(kamera.stream_url || ''); setEditStream(false); }}
              className="px-2 py-1 rounded-lg text-[9px] shrink-0"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>✕</button>
          </div>
        ) : kamera.stream_url ? (
          <div className="flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-lg text-[8px] font-black"
              style={{ background: 'rgba(0,255,136,0.12)', color: 'var(--color-green)', border: '1px solid rgba(0,255,136,0.3)' }}>
              ● STREAM AKTIF
            </span>
            {savedField === 'stream' && <CheckCircle size={9} color="var(--color-green)" />}
            <button onClick={() => setEditStream(true)}
              className="ml-auto text-[9px] font-bold hover:opacity-80"
              style={{ color: 'var(--color-cyan)' }}>
              Ubah
            </button>
          </div>
        ) : (
          <button onClick={() => setEditStream(true)}
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[9px] font-black transition-all hover:opacity-80 w-full"
            style={{ background: 'rgba(0,212,255,0.07)', border: '1px dashed rgba(0,212,255,0.25)', color: 'var(--color-cyan)' }}>
            <LinkIcon size={9} />
            Atur URL stream
          </button>
        )}
      </div>

      {/* Section 5 — Footer: open in NeoVision */}
      {onOpenInNeoVision && (
        <button onClick={onOpenInNeoVision}
          className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[9px] font-black transition-all hover:opacity-100 mt-auto"
          style={{ background: 'transparent', border: '1px solid var(--border-dim)', color: 'var(--color-cyan)', opacity: 0.7 }}>
          <Eye size={10} /> Lihat di Vision <ChevronRight size={9} />
        </button>
      )}
    </div>
  );
};

const CCTVTab = ({ kameras, setKameras, setPage }) => {
  const { t } = useApp();
  const [addingNew,     setAddingNew]     = useState(false);
  const [newForm,       setNewForm]       = useState({ nama: '', lokasi: '', status: 'ONLINE' });
  const [saving,        setSaving]        = useState(false);
  const [showBatchVideo, setShowBatchVideo] = useState(false);

  const totalKamera   = kameras.length;
  const onlineCount   = kameras.filter(k => k.status === 'ONLINE').length;
  const totalRekaman  = kameras.reduce((s, k) => s + (k.playlist?.length || 0), 0);

  const handleStatusToggle = async (kamera) => {
    const newStatus = kamera.status === 'ONLINE' ? 'OFFLINE' : 'ONLINE';
    const { error } = await supabase.from('kamera').update({ status: newStatus }).eq('id', kamera.id);
    if (error) { console.warn('[handleStatusToggle] fail:', error.message); alert(`Gagal toggle status: ${error.message}`); return; }
    setKameras(prev => prev.map(k => k.id === kamera.id ? { ...k, status: newStatus } : k));
  };

  const handleUpdate = (updated) => {
    setKameras(prev => prev.map(k => k.id === updated.id ? updated : k));
  };

  const handleDelete = (id) => {
    setKameras(prev => prev.filter(k => k.id !== id));
  };

  const handleAddNew = async () => {
    if (!newForm.nama.trim()) return;
    setSaving(true);
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    const id = `CAM-${Date.now().toString(36).toUpperCase()}-${rand}`;
    const { data, error } = await supabase.from('kamera').insert({
      id,
      nama:        newForm.nama.trim(),
      lokasi:      newForm.lokasi.trim() || 'Tidak diketahui',
      status:      newForm.status,
      playlist:    [],
      total_videos: 0,
    }).select().single();
    setSaving(false);
    if (error) {
      alert(`Gagal tambah kamera: ${error.message}`);
      return;
    }
    if (data) setKameras(prev => [...prev, data]);
    setNewForm({ nama: '', lokasi: '', status: 'ONLINE' });
    setAddingNew(false);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Stats bar */}
      <div className="flex items-center gap-4 px-4 py-2 shrink-0" style={{ borderBottom: '1px solid var(--border-dim)', background: 'var(--color-surface)' }}>
        <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
          Total: <span style={{ color: 'var(--text-mid)' }}>{totalKamera} kamera</span>
        </span>
        <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
          Online: <span style={{ color: 'var(--color-green)' }}>{onlineCount}</span>
        </span>
        <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
          Total Rekaman: <span style={{ color: '#ff6b2b' }}>{totalRekaman} file</span>
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--border-dim)' }}>
        <button onClick={() => setShowBatchVideo(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
          style={{ background: 'rgba(124,77,255,0.10)', border: '1px solid rgba(124,77,255,0.25)', color: '#7c4dff' }}>
          <UploadCloud size={9} /> {t('upload_cctv_label')}
        </button>
        <div className="flex-1" />
        <button onClick={() => setAddingNew(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
          style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', color: 'var(--color-green)' }}>
          <Plus size={9} /> {t('add_camera_btn')}
        </button>
      </div>

      {/* Inline add form */}
      {addingNew && (
        <div className="flex items-center gap-2 px-4 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--border-dim)', background: 'rgba(0,255,136,0.03)' }}>
          <input value={newForm.nama} onChange={e => setNewForm(f => ({ ...f, nama: e.target.value }))}
            placeholder={t('cam_name_ph')}
            onKeyDown={e => e.key === 'Enter' && handleAddNew()}
            className="rounded-xl px-3 py-2 text-xs outline-none"
            style={{ background: 'var(--color-surface)', border: '1px solid rgba(0,255,136,0.3)', color: 'var(--text-mid)', width: 150 }} />
          <input value={newForm.lokasi} onChange={e => setNewForm(f => ({ ...f, lokasi: e.target.value }))}
            placeholder={t('cam_loc_ph')}
            className="rounded-xl px-3 py-2 text-xs outline-none flex-1"
            style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }} />
          <select value={newForm.status} onChange={e => setNewForm(f => ({ ...f, status: e.target.value }))}
            className="rounded-xl px-3 py-2 text-xs outline-none shrink-0"
            style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)', width: 100 }}>
            <option value="ONLINE">ONLINE</option>
            <option value="OFFLINE">OFFLINE</option>
          </select>
          <button onClick={handleAddNew} disabled={saving || !newForm.nama.trim()}
            className="px-4 py-2 rounded-xl text-xs font-black shrink-0"
            style={{ background: 'var(--color-green)', color: '#02060e', opacity: saving || !newForm.nama.trim() ? 0.6 : 1 }}>
            Simpan
          </button>
          <button onClick={() => setAddingNew(false)}
            className="px-3 py-2 rounded-xl text-xs font-black shrink-0"
            style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
            Batal
          </button>
        </div>
      )}

      {/* Camera grid */}
      {kameras.length === 0 ? (
        /* Belum ada kamera terdaftar: dinding Monitor CCTV (sama seperti tab
           Monitor CCTV di Vision). Grid juga tersedia di halaman Datacenter,
           permintaan Andra 29 Okt 2026. */
        <div className="flex-1 min-h-0 flex flex-col p-4">
          <CctvWall />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto c-scroll p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {kameras.map(k => (
              <KameraCard key={k.id} kamera={k}
                onStatusToggle={handleStatusToggle}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onOpenInNeoVision={setPage ? () => setPage('neovision') : undefined} />
            ))}
          </div>
        </div>
      )}

      {showBatchVideo && (
        <BatchVideoUploadModal
          kameras={kameras}
          onClose={() => setShowBatchVideo(false)}
          onDone={(data) => { setKameras(data); setShowBatchVideo(false); }}
        />
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const DataCenter = ({ kameras, setKameras, setPage }) => {
  const { t, lang } = useApp();
  const [tab, setTab] = useState('penduduk');
  const [batchType, setBatchType] = useState(null);

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--color-bg)' }}>
      <div className="shrink-0 flex items-center justify-between px-4 pt-3 pb-0">
        <div className="flex items-center gap-1">
          {[
            { id: 'penduduk', label: t('dc_tab_penduduk') },
            { id: 'cctv',     label: t('dc_tab_cctv') },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setTab(id)}
              className="px-4 py-2 text-xs font-black transition-all rounded-t-xl"
              style={{ background: tab === id ? 'var(--color-card)' : 'transparent', color: tab === id ? 'var(--color-cyan)' : 'var(--text-mute)', borderBottom: tab === id ? '2px solid var(--color-cyan)' : '2px solid transparent' }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'penduduk' && (
          <div className="flex items-center gap-2 pb-1">
            <span className="text-[8px] uppercase tracking-widest font-mono mr-1" style={{ color: 'var(--text-mute)' }}>{t('template_csv')}</span>
            <button onClick={downloadPendudukTemplate}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
              style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', color: 'var(--color-cyan)' }}>
              <Download size={9} /> {t('btn_penduduk')}
            </button>
            <button onClick={downloadKendaraanTemplate}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
              style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', color: 'var(--color-cyan)' }}>
              <Car size={9} /> {t('btn_kendaraan')}
            </button>
            <div className="w-px h-4 mx-1" style={{ background: 'var(--border-dim)' }} />
            <button onClick={() => setBatchType('penduduk')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all hover:opacity-80"
              style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', color: 'var(--color-green)' }}>
              <Download size={9} /> {t('btn_import_csv')}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-hidden flex flex-col"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', margin: '0 16px 16px', borderRadius: '0 12px 12px 12px' }}>
        {tab === 'penduduk' && <PendudukTab />}
        {tab === 'cctv'     && <CCTVTab kameras={kameras} setKameras={setKameras} setPage={setPage} />}
      </div>

      {batchType && (
        <BatchImportModal
          type={batchType}
          onClose={() => setBatchType(null)}
          onImported={() => setBatchType(null)}
        />
      )}
    </div>
  );
};

export default DataCenter;
